/**
 * Chart Theme Bridge
 * Reads CSS design tokens and maps them to ECharts theme configuration.
 * Ensures charts stay consistent with the component library's token system.
 */

import * as echarts from "echarts";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Resolve a CSS custom property from the document root */
function getCSSVar(name: string, el?: HTMLElement): string {
  const target = el ?? document.documentElement;
  return getComputedStyle(target).getPropertyValue(name).trim();
}

// ---------------------------------------------------------------------------
// Pure-math OKLCH → sRGB hex conversion (no browser API dependency)
// ---------------------------------------------------------------------------

/** Clamp a number to [0, 1] */
function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

/** Linear sRGB → sRGB gamma correction */
function linearToSrgb(c: number): number {
  return c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
}

/** Convert OKLab (L, a, b) to linear sRGB (r, g, b) */
function oklabToLinearSrgb(L: number, a: number, b: number): [number, number, number] {
  // OKLab → LMS (cube-root space)
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.2914855480 * b;

  // LMS (cube root) → LMS
  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;

  // LMS → linear sRGB
  return [
    +4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s,
  ];
}

/** Convert OKLCH (L, C, H-in-degrees) → hex #rrggbb */
function oklchToHex(L: number, C: number, H: number): string {
  const hRad = (H * Math.PI) / 180;
  const a = C * Math.cos(hRad);
  const b = C * Math.sin(hRad);
  const [lr, lg, lb] = oklabToLinearSrgb(L, a, b);
  const r = Math.round(clamp01(linearToSrgb(lr)) * 255);
  const g = Math.round(clamp01(linearToSrgb(lg)) * 255);
  const bv = Math.round(clamp01(linearToSrgb(lb)) * 255);
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${bv.toString(16).padStart(2, "0")}`;
}

/** Parse an rgb/rgba string (comma or space separated) → hex */
function parseRgbToHex(str: string): string | null {
  // "rgb(31, 101, 192)" or "rgb(31 101 192)" or "rgba(31, 101, 192, 1)"
  const m = str.match(/rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/);
  if (!m) return null;
  const r = Math.round(parseFloat(m[1]));
  const g = Math.round(parseFloat(m[2]));
  const b = Math.round(parseFloat(m[3]));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

/**
 * Convert any CSS color value to a 7-char hex string.
 *
 * Strategy (in order):
 *  1. Hex pass-through
 *  2. oklch() — pure math conversion (no browser API)
 *  3. rgb()/rgba() — direct parse
 *  4. hsl()/hsla() — math conversion
 *  5. color(srgb ...) — direct parse
 *  6. Fallback grey
 */
function resolveColor(cssValue: string): string {
  if (!cssValue) return "#888888";

  const v = cssValue.trim();

  // 1. Hex pass-through
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v;
  if (/^#[0-9a-fA-F]{3}$/.test(v)) {
    const [, r, g, b] = v.split("");
    return `#${r}${r}${g}${g}${b}${b}`;
  }

  // 2. oklch(L C H) — parse and convert via pure math
  // Handles both decimal (0.55) and percentage (55%) lightness formats,
  // since Lightning CSS normalizes decimals to percentages in production builds.
  const oklchMatch = v.match(/oklch\(\s*([\d.]+)(%?)\s+([\d.]+)(%?)\s+([\d.]+)/);
  if (oklchMatch) {
    let L = parseFloat(oklchMatch[1]);
    if (oklchMatch[2] === "%") L /= 100; // 55% → 0.55
    let C = parseFloat(oklchMatch[3]);
    if (oklchMatch[4] === "%") C = C / 100 * 0.4; // percentage chroma (rare)
    const H = parseFloat(oklchMatch[5]);
    return oklchToHex(L, C, H);
  }

  // 3. rgb()/rgba()
  const rgbResult = parseRgbToHex(v);
  if (rgbResult) return rgbResult;

  // 4. hsl()/hsla()
  const hslMatch = v.match(/hsla?\(\s*([\d.]+)[\s,]+([\d.]+)%?[\s,]+([\d.]+)%?/);
  if (hslMatch) {
    const h = parseFloat(hslMatch[1]) / 360;
    const s = parseFloat(hslMatch[2]) / 100;
    const l = parseFloat(hslMatch[3]) / 100;
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
    const g = Math.round(hue2rgb(p, q, h) * 255);
    const b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);
    return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
  }

  // 5. color(srgb r g b)
  const srgbMatch = v.match(/color\(srgb\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)/);
  if (srgbMatch) {
    const r = Math.round(clamp01(parseFloat(srgbMatch[1])) * 255);
    const g = Math.round(clamp01(parseFloat(srgbMatch[2])) * 255);
    const b = Math.round(clamp01(parseFloat(srgbMatch[3])) * 255);
    return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
  }

  return "#888888";
}

/** Generate tints (lighter) and shades (darker) to extend a palette */
function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

function rgbToHex(r: number, g: number, b: number): string {
  return (
    "#" +
    [r, g, b]
      .map((c) => Math.max(0, Math.min(255, Math.round(c))))
      .map((c) => c.toString(16).padStart(2, "0"))
      .join("")
  );
}

function tint(hex: string, factor: number): string {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(
    r + (255 - r) * factor,
    g + (255 - g) * factor,
    b + (255 - b) * factor
  );
}

function shade(hex: string, factor: number): string {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(r * (1 - factor), g * (1 - factor), b * (1 - factor));
}

// ---------------------------------------------------------------------------
// Token Resolution
// ---------------------------------------------------------------------------

export interface ChartTokens {
  // Core palette (5 base + extended)
  colors: string[];
  // Typography
  fontFamily: string;
  fontSize: number;
  // Foreground colors
  textColor: string;
  textColorSecondary: string;
  // Backgrounds & borders
  backgroundColor: string;
  borderColor: string;
  // Semantic status
  successColor: string;
  warningColor: string;
  errorColor: string;
  infoColor: string;
  // Spacing
  containerPadding: number;
  // Animation
  animationDuration: number;
  animationEasing: string;
}

/**
 * Read all relevant CSS tokens and return a resolved `ChartTokens` object.
 * Safe to call multiple times (lightweight – just getComputedStyle reads).
 */
export function resolveChartTokens(el?: HTMLElement): ChartTokens {
  const base5 = [
    resolveColor(getCSSVar("--chart-1", el)),
    resolveColor(getCSSVar("--chart-2", el)),
    resolveColor(getCSSVar("--chart-3", el)),
    resolveColor(getCSSVar("--chart-4", el)),
    resolveColor(getCSSVar("--chart-5", el)),
  ];

  // Extend to 10 colors by generating a tint and a shade per base color
  const extended = [
    ...base5,
    tint(base5[0], 0.35),
    shade(base5[1], 0.25),
    tint(base5[2], 0.4),
    shade(base5[3], 0.2),
    tint(base5[4], 0.3),
  ];

  const fontFamily =
    getCSSVar("--font-sans", el) || "Urbanist, system-ui, sans-serif";

  return {
    colors: extended,
    fontFamily,
    fontSize: 12,
    textColor: resolveColor(getCSSVar("--foreground", el)),
    textColorSecondary: resolveColor(getCSSVar("--muted-foreground", el)),
    backgroundColor: resolveColor(getCSSVar("--background", el)),
    borderColor: resolveColor(getCSSVar("--border", el)),
    successColor: resolveColor(getCSSVar("--color-success", el) || "#22c55e"),
    warningColor: resolveColor(getCSSVar("--color-warning", el) || "#f59e0b"),
    errorColor: resolveColor(getCSSVar("--color-error", el) || "#ef4444"),
    infoColor: resolveColor(getCSSVar("--color-info", el) || "#3b82f6"),
    containerPadding: 16,
    animationDuration: 800,
    animationEasing: "cubicInOut",
  };
}

// ---------------------------------------------------------------------------
// ECharts Theme Object
// ---------------------------------------------------------------------------

/**
 * Build a complete ECharts theme object from resolved tokens.
 * Covers: color palette, text styles, axis, legend, tooltip, grid, and
 * type-specific series defaults (bar, line, pie, gauge, treemap, etc.).
 */
export function buildEChartsTheme(tokens: ChartTokens): Record<string, any> {
  const { colors, fontFamily, fontSize, textColor, textColorSecondary, backgroundColor, borderColor } = tokens;

  return {
    color: colors,
    backgroundColor: "transparent",

    // --- Text Style ---
    textStyle: {
      fontFamily,
      fontSize,
      color: textColor,
    },

    // --- Title ---
    title: {
      textStyle: {
        fontFamily,
        fontSize: 16,
        fontWeight: 600,
        color: textColor,
      },
      subtextStyle: {
        fontFamily,
        fontSize: 12,
        color: textColorSecondary,
      },
      top: 0,
      left: 0,
      padding: [0, 0, 16, 0],
    },

    // --- Legend ---
    legend: {
      textStyle: {
        fontFamily,
        fontSize: 12,
        color: textColorSecondary,
      },
      icon: "roundRect",
      itemWidth: 12,
      itemHeight: 12,
      itemGap: 16,
      top: "auto",
      bottom: 0,
    },

    // --- Tooltip ---
    tooltip: {
      backgroundColor,
      borderColor,
      borderWidth: 1,
      borderRadius: 8,
      padding: [8, 12],
      textStyle: {
        fontFamily,
        fontSize: 12,
        color: textColor,
      },
      extraCssText: `box-shadow: 0 4px 12px rgba(0,0,0,0.08); backdrop-filter: blur(8px);`,
    },

    // --- Grid ---
    grid: {
      top: 48,
      right: 16,
      bottom: 48,
      left: 16,
      containLabel: true,
    },

    // --- Category Axis ---
    categoryAxis: {
      axisLine: { show: true, lineStyle: { color: borderColor } },
      axisTick: { show: false },
      axisLabel: {
        fontFamily,
        fontSize: 11,
        color: textColorSecondary,
      },
      splitLine: { show: false },
    },

    // --- Value Axis ---
    valueAxis: {
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        fontFamily,
        fontSize: 11,
        color: textColorSecondary,
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: borderColor,
          type: "dashed",
          opacity: 0.6,
        },
      },
    },

    // --- Bar ---
    bar: {
      barMaxWidth: 32,
      itemStyle: {
        borderRadius: [4, 4, 0, 0],
      },
      emphasis: {
        focus: "series",
        blurScope: "coordinateSystem",
        itemStyle: {
          shadowBlur: 12,
          shadowOffsetY: 3,
          shadowColor: "rgba(0,0,0,0.2)",
          borderWidth: 1,
          borderColor: "rgba(255,255,255,0.4)",
        },
      },
    },

    // --- Line ---
    line: {
      smooth: true,
      symbol: "circle",
      symbolSize: 6,
      lineStyle: { width: 2.5 },
      emphasis: {
        focus: "series",
        blurScope: "coordinateSystem",
        lineStyle: { width: 4 },
        itemStyle: {
          borderWidth: 3,
          borderColor: "#fff",
          shadowBlur: 8,
          shadowColor: "rgba(0,0,0,0.18)",
        },
      },
    },

    // --- Pie ---
    // NOTE: scale/scaleSize intentionally omitted to prevent hover flickering.
    // Emphasis uses shadow + border only — no geometry resize.
    pie: {
      radius: ["0%", "70%"],
      itemStyle: {
        borderColor: backgroundColor,
        borderWidth: 3,
        borderRadius: 4,
      },
      label: {
        fontFamily,
        fontSize: 12,
        color: textColor,
      },
      emphasis: {
        focus: "self",
        itemStyle: {
          shadowBlur: 16,
          shadowOffsetY: 4,
          shadowColor: "rgba(0,0,0,0.25)",
          borderWidth: 3,
          borderColor: "rgba(255,255,255,0.6)",
        },
      },
    },

    // --- Gauge ---
    gauge: {
      axisLine: { lineStyle: { width: 14 } },
      axisTick: { show: false },
      axisLabel: { fontFamily, fontSize: 11, color: textColorSecondary },
      pointer: { width: 5, length: "60%", itemStyle: { color: textColor } },
      title: { fontFamily, fontSize: 14, color: textColorSecondary, offsetCenter: [0, "72%"] },
      detail: { fontFamily, fontSize: 28, fontWeight: 700, color: textColor, offsetCenter: [0, "42%"] },
      emphasis: {
        itemStyle: { shadowBlur: 10, shadowColor: "rgba(0,0,0,0.18)" },
      },
    },

    // --- Treemap ---
    treemap: {
      itemStyle: {
        borderColor: backgroundColor,
        borderWidth: 2,
        gapWidth: 2,
      },
      label: { fontFamily, fontSize: 12, color: "#fff" },
      breadcrumb: {
        itemStyle: { color: colors[0], textStyle: { color: "#fff" } },
      },
    },
  };
}

// ---------------------------------------------------------------------------
// Theme Registration
// ---------------------------------------------------------------------------

const THEME_NAME = "bluecopa";
let _registered = false;

/**
 * Register (or re-register) the BlueCopa ECharts theme.
 * Call this on mount and whenever the CSS theme / dark-mode changes.
 */
export function registerChartTheme(el?: HTMLElement): string {
  const tokens = resolveChartTokens(el);
  const theme = buildEChartsTheme(tokens);
  echarts.registerTheme(THEME_NAME, theme);
  _registered = true;
  return THEME_NAME;
}

/** Check if the theme has been registered */
export function isChartThemeRegistered(): boolean {
  return _registered;
}

/** The theme name to pass to ECharts instances */
export const CHART_THEME_NAME = THEME_NAME;

export type { echarts };
